import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  TagIcon,
} from "@heroicons/react/20/solid";
import type {PropsWithChildren, ReactNode, Ref} from "react";
import {AdminPage} from "../components/AdminPage";
import {getOtsActionButtonClassName, OtsActionButton, OtsIconActionButton} from "../components/OtsButton";
import {OtsPopover} from "../components/OtsPopover";
import {OtsTooltip} from "../components/OtsTooltip";
import {OtsThemeRoot} from "../theme/OtsTheme";
import type {ContainerStatusDisplayOptions} from "./container-status-display";
import {ContainerStatusDisplayMenu} from "./ContainerStatusDisplayMenu";

export type ContainerStatusWorkspaceNotice = {
  id: string;
  tone: "info" | "warning" | "danger";
  message: string;
};

export type ContainerStatusWorkspaceProps = PropsWithChildren<{
  containerCount: number;
  /** Already formatted for the region, e.g. "Oct 26 14:20". */
  lastFetched?: string;
  isRefreshing?: boolean;
  showSearch: boolean;
  onSearchClick: () => void;
  filterActive: boolean;
  onFilterClick: () => void;
  tagFilterActive: boolean;
  onTagFilterClick: () => void;
  onRefresh: () => void;
  onExport: () => void;
  exportDisabled?: boolean;
  /** Fullscreen for the grid; hosts without it omit the button. */
  onFullscreen?: () => void;
  /** Data caveats, such as readings that could not be loaded, shown as icons beside the count. */
  notices?: ContainerStatusWorkspaceNotice[];
  /** The allocatable load settings in effect, shown on hover. */
  settingsSummary?: ReactNode;
  /** Filters and page context below the header, outside the grid sizing and scrolling surface. */
  context?: ReactNode;
  /** How the grid marks rows, such as stale polls; the Display options button shows when both are given. */
  display?: ContainerStatusDisplayOptions;
  onDisplayChange?: (display: ContainerStatusDisplayOptions) => void;
  /** Beside the grid, e.g. the open container's details panel. */
  aside?: ReactNode;
  /** The hosted grid portals its column controls here. */
  gridControlsRef?: Ref<HTMLDivElement>;
}>;

const noticeIcon = (tone: ContainerStatusWorkspaceNotice["tone"]) => {
  const color = tone === "danger"
    ? "text-[var(--ots-error)]"
    : tone === "warning" ? "text-[var(--ots-warning)]" : "text-[var(--ots-primary)]";
  const Icon = tone === "info" ? InformationCircleIcon : ExclamationTriangleIcon;
  return <Icon aria-hidden="true" className={`size-4 ${color}`} />;
};

/** Container Status shell: the same compact workspace header as Tickets and Dispatch, with the grid filling the rest. */
export const ContainerStatusWorkspace = ({
  containerCount,
  lastFetched,
  isRefreshing,
  showSearch,
  onSearchClick,
  filterActive,
  onFilterClick,
  tagFilterActive,
  onTagFilterClick,
  onRefresh,
  onExport,
  exportDisabled,
  onFullscreen,
  notices = [],
  settingsSummary,
  context,
  gridControlsRef,
  display,
  onDisplayChange,
  aside,
  children,
}: ContainerStatusWorkspaceProps) => (
  <OtsThemeRoot className="h-full min-h-0 min-w-0">
    <AdminPage
      title="Container Status"
      status={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--ots-text-muted)]">
          <span className="tabular-nums">{containerCount.toLocaleString()} {containerCount === 1 ? "Container" : "Containers"}</span>
          <span aria-hidden="true">|</span>
          <span className="tabular-nums">Last fetched: {lastFetched ?? "—"}</span>
          {isRefreshing && <span role="status">Refreshing…</span>}
          {settingsSummary && (
            <OtsTooltip content={settingsSummary} density="compact" panelClassName="max-w-xs whitespace-pre-wrap">
              <span tabIndex={0} aria-label="Allocatable load settings" className="inline-flex">
                <AdjustmentsHorizontalIcon aria-hidden="true" className="size-4" />
              </span>
            </OtsTooltip>
          )}
          {notices.map((notice) => (
            <OtsTooltip key={notice.id} content={notice.message} density="compact" panelClassName="max-w-xs">
              <span tabIndex={0} role="img" aria-label={notice.message} className="inline-flex">
                {noticeIcon(notice.tone)}
              </span>
            </OtsTooltip>
          ))}
        </span>
      }
      fullWidth
      layout="fill"
      density="compact"
      headerLayout="workspace"
      actions={
        <>
          <div role="group" aria-label="Find containers" className="flex items-center gap-1">
            <OtsIconActionButton
              size="compact"
              variant={showSearch ? "tonal" : "text"}
              icon={<MagnifyingGlassIcon />}
              label="Search containers"
              aria-pressed={showSearch}
              onClick={onSearchClick}
            />
            <OtsIconActionButton
              size="compact"
              variant={filterActive ? "tonal" : "text"}
              icon={<FunnelIcon />}
              label={filterActive ? "Filter containers (active)" : "Filter containers"}
              aria-pressed={filterActive}
              onClick={onFilterClick}
            />
            <OtsIconActionButton
              size="compact"
              variant={tagFilterActive ? "tonal" : "text"}
              icon={<TagIcon />}
              label={tagFilterActive ? "Filter by tags (active)" : "Filter by tags"}
              aria-pressed={tagFilterActive}
              onClick={onTagFilterClick}
            />
          </div>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
          <div className="flex items-center gap-1">
            <div ref={gridControlsRef} role="group" aria-label="Container grid controls" />
            {display && onDisplayChange && <ContainerStatusDisplayMenu display={display} onDisplayChange={onDisplayChange} />}
          </div>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
          <div role="group" aria-label="Container status actions" className="flex items-center gap-1">
            <OtsIconActionButton
              size="compact"
              variant="text"
              icon={<ArrowPathIcon className={isRefreshing ? "animate-spin" : undefined} />}
              label="Refresh container status"
              disabled={isRefreshing}
              onClick={onRefresh}
            />
            {onFullscreen && (
              <OtsIconActionButton
                size="compact"
                variant="text"
                icon={<ArrowsPointingOutIcon />}
                label="Fullscreen grid"
                onClick={onFullscreen}
              />
            )}
            <OtsPopover
              density="compact"
              placement="bottom end"
              ariaLabel="More container status actions"
              tooltip="More container status actions"
              trigger={<EllipsisHorizontalIcon aria-hidden="true" className="size-[18px]" />}
              buttonClassName={getOtsActionButtonClassName("text", "compact", "!size-8 !p-0")}
              panelClassName="flex min-w-44 flex-col gap-1"
            >
              {({close}) => (
                <OtsActionButton
                  size="compact"
                  variant="text"
                  icon={<ArrowDownTrayIcon />}
                  className="w-full justify-start whitespace-nowrap"
                  disabled={exportDisabled}
                  onClick={() => {
                    close();
                    onExport();
                  }}
                >
                  Export
                </OtsActionButton>
              )}
            </OtsPopover>
          </div>
        </>
      }
    >
      {context != null && <div className="mt-2 shrink-0">{context}</div>}
      {/* Legacy hosts reset section display; keep the grid on a definite flex sizing chain. */}
      <div className="mt-2 !flex min-h-0 min-w-0 flex-1 gap-2">
        <section
          aria-label="Container status data grid"
          aria-busy={isRefreshing || undefined}
          className="relative !flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]"
        >
          {children}
        </section>
        {aside != null && (
          <div className="!flex min-h-0 w-[420px] max-w-[50%] shrink-0 flex-col overflow-hidden rounded-lg border border-[var(--ots-outline-variant)]">
            {aside}
          </div>
        )}
      </div>
    </AdminPage>
  </OtsThemeRoot>
);
